import * as _ from 'lodash';
import * as hash from 'object-hash';
import { Component } from 'react';
import { connect, Dispatch } from 'react-redux';
import { AppActionCreators, CenterActionCreators, IApplicationState, } from '@nkr/store/index';
import { ISrtrData, IUpdateSrtrDataRequest } from '@nkr/models/center-profile/index';
import { ISrtrDataValidation } from './validation';
import { DataType } from '../../data-type';
import { Form, FormGroup, Panel } from 'react-bootstrap';
import UploadFile, { NewFileDisplayType, UploadFileType } from '../../upload-file';
import CenterTextInput from '../../center-text-input';
import { ICenterSrtrDataResponse } from '@nkr/store/center/actions-center-profile';
import { toast } from 'react-toastify';
import { Validate } from '../../validate';
import { initialSrtrData, initialSrtrValidationData } from '../initial-data';
import CenterProfileNav from '../edit-nav';
import { profileNavKey } from '../../profile-nav-key';
import { IErrorItem } from '../../error-item';
import { VersionedFileEntityType } from '@nkr/enums/versioned-file-entity-type';
import { downloadFile } from '@nkr/services/download-file';

interface IProps {
    centerId: number,
    isAdmin: boolean,
    isAdminUser: boolean,
    hasDataChanges: boolean,
    onTabClick: (href: string) => void,
    onDataChanged: (hasDataChanged: boolean) => void,
    updateErrors: (list: IErrorItem[]) => void
}

interface IStateProps { }

interface IDispatchProps {
    showLoading: (message?: string) => Promise<void>,
    hideLoading: () => void,
    getSrtrData: (centerId: number) => Promise<ICenterSrtrDataResponse>,
    updateSrtrData: (data: IUpdateSrtrDataRequest) => Promise<ICenterSrtrDataResponse>,
    getVersionedFile: (centerId: number, id: number) => Promise<any>
}

type TProps = IProps & IStateProps & IDispatchProps;

interface IState {
    data: ISrtrData,
    validation: ISrtrDataValidation,
    hash: string,
    isDirty: boolean,
    isValid: boolean
}

class CenterSrtrDataTab extends Component<TProps, IState> {
    readonly state: IState = {
        data: initialSrtrData,
        validation: initialSrtrValidationData,
        hash: '',
        isDirty: false,
        isValid: true
    }

    componentDidMount = () => {
        this.getData();
    }

    getData = () => {
        this.props.showLoading('Getting center\'s SRTR data, one moment please...').then(() => {
            this.props.getSrtrData(this.props.centerId).then(response => {
                this.setState({
                    data: response.data.results,
                    hash: hash(response.data.results)
                });

                this.props.hideLoading();
            });
        });
    }

    save = () => {
        let {
            dcAdultGraftSurvival1, dcAdultGraftSurvival3,
            dcPediatricGraftSurvival1, dcPediatricGraftSurvival3,
            adultGraftSurvival1, adultGraftSurvival3,
            pediatricGraftSurvival1, pediatricGraftSurvival3,
            dcAdultPatientSurvival1, dcAdultPatientSurvival3,
            dcPediatricPatientSurvival1, dcPediatricPatientSurvival3,
            srtrSourceDate: srtrSourceDate,
            graftSurvivalNewFile, patientSurvivalNewFile
        } = this.state.data;

        let { file: graftSurvivalFile, version: graftSurvivalFileVersion, date: graftSurvivalFileDate } = Validate.getFileInfo(graftSurvivalNewFile);
        let { file: srtrPatientSurvivalFile, version: srtrPatientSurvivalFileVersion, date: srtrPatientSurvivalFileDate } = Validate.getFileInfo(patientSurvivalNewFile);

        let data: IUpdateSrtrDataRequest = {
            id: this.props.centerId,
            dcAdultGraftSurvival1, dcAdultGraftSurvival3,
            dcPediatricGraftSurvival1, dcPediatricGraftSurvival3,
            adultGraftSurvival1, adultGraftSurvival3,
            pediatricGraftSurvival1, pediatricGraftSurvival3,
            dcAdultPatientSurvival1, dcAdultPatientSurvival3,
            dcPediatricPatientSurvival1, dcPediatricPatientSurvival3,
            srtrSourceDate,
            graftSurvivalFile, graftSurvivalFileVersion, graftSurvivalFileDate,
            srtrPatientSurvivalFile, srtrPatientSurvivalFileVersion, srtrPatientSurvivalFileDate
        };

        this.props.showLoading('Saving SRTR data, one moment please...').then(() => {
            this.props.updateSrtrData(data).then(response => {
                this.props.hideLoading();

                if (response.data.success) {
                    this.setState({
                        data: response.data.results,
                        hash: hash(response.data.results),
                    }, () => {
                        toast.success('SRTR data updated successfully');

                        this.props.onDataChanged(false);
                    });
                }
            });
        });
    }

    validateBeforeSave = () => {
        let validation = Validate.validateSrtrData(this.state.data);
        let isValid = Validate.isSrtrDataValid(validation);
        let errorList = Validate.getInvalidSrtrDataItems(validation).filter(x => x.show);

        this.setState({
            validation,
            isValid,
            isDirty: true,
        }, () => {
            this.props.updateErrors(errorList);

            if (this.state.isValid) {
                this.save();
            } else {
                window.scrollTo(0, 0);
            }
        });
    }

    handleCenterTextInputDataChange = (name: string, value: string, type: DataType) => {
        var data = _.cloneDeep(this.state.data);

        if (type == DataType.Float) {
            data[name] = parseFloat(value);
        }

        if (type == DataType.String) {
            data[name] = value;
        }

        this.setState({
            data
        });

        let newHash = hash(data);

        this.props.onDataChanged(this.state.hash != newHash);
    };

    handleFileChange = (fileEntityType: VersionedFileEntityType, file: File, documentsVersionId: number, date: string) => {
        var data = _.cloneDeep(this.state.data);

        if (fileEntityType == VersionedFileEntityType.CenterPatientSurvival) {
            data.patientSurvivalNewFile = {
                file,
                documentsVersionId,
                date
            };
        }

        if (fileEntityType == VersionedFileEntityType.CenterGraftSurvival) {
            data.graftSurvivalNewFile = {
                file,
                documentsVersionId,
                date
            };
        }

        this.setState({
            data
        });

        let newHash = hash(data);

        this.props.onDataChanged(this.state.hash != newHash);
    };

    handleFileRemove = (fileEntityType: VersionedFileEntityType) => {
        var data = _.cloneDeep(this.state.data);

        if (fileEntityType == VersionedFileEntityType.CenterPatientSurvival) {
            data.patientSurvivalNewFile = null;
        }

        if (fileEntityType == VersionedFileEntityType.CenterGraftSurvival) {
            data.graftSurvivalNewFile = null;
        }

        this.setState({
            data
        });
    };

    handleFileDownload = (id: number) => {
        this.props.getVersionedFile(this.props.centerId, id).then(response => {
            downloadFile(response.data, `file.pdf`, response.headers.contentType);
        })
    }


    render = () => {
        return (
            <>
                <CenterProfileNav
                    activeKey={profileNavKey.SRTR}
                    isAdmin={this.props.isAdmin}
                    isAdminUser={this.props.isAdminUser}
                    centerId={this.props.centerId}
                    hasChanges={this.props.hasDataChanges}
                    onTabClick={this.props.onTabClick}
                    onSave={this.validateBeforeSave}
                />

                <div style={{ padding: 10, borderWidth: '1px 1px 1px 1px', borderStyle: 'solid', borderColor: '#ddd', borderRadius: 4 }}>
                    <Form>
                        <div className='row'>
                            <div className='col-xs-12 col-sm-8 col-sm-offset-2'>
                                <h4>Files</h4>
                            </div>
                        </div>

                        <div className='row center-srtr'>
                            <div className='col-xs-12 col-sm-4 col-sm-offset-2'>
                                <Panel>
                                    <Panel.Heading>Graft Survival</Panel.Heading>
                                    <Panel.Body>
                                        <UploadFile
                                            // elementName='graftSurvivalNewFile'
                                            fileEntityType={VersionedFileEntityType.CenterGraftSurvival}
                                            valid={true}
                                            labelText=''
                                            modalTitle='Graft Survival File'
                                            withVersionHistory={true}
                                            disable={false}
                                            type={UploadFileType.WithVersionControl}
                                            documentsVersions={this.state.data.graftSurvivalDocumentsVersions}
                                            dimensions={{ label: 0, existing: 6, select: 6, new: { size: 12, offset: 0 } }}
                                            onSelectFile={this.handleFileChange}
                                            onRemoveFile={this.handleFileRemove}
                                            versionedFiles={this.state.data.graftSurvivalVersionedFiles}
                                            newFile={this.state.data.graftSurvivalNewFile}
                                            marginBottom={0}
                                            newFileDisplayType={NewFileDisplayType.SrtrData}
                                            onDownloadFile={this.handleFileDownload}
                                        />
                                    </Panel.Body>
                                </Panel>
                            </div>
                            <div className='col-xs-12 col-sm-4'>
                                <Panel>
                                    <Panel.Heading>Patient Survival</Panel.Heading>
                                    <Panel.Body>
                                        <UploadFile
                                            // elementName='patientSurvivalNewFile'
                                            fileEntityType={VersionedFileEntityType.CenterPatientSurvival}
                                            valid={true}
                                            labelText=''
                                            modalTitle='SRTR Patient Survival File'
                                            withVersionHistory={true}
                                            disable={false}
                                            type={UploadFileType.WithVersionControl}
                                            documentsVersions={this.state.data.patientSurvivalDocumentsVersions}
                                            dimensions={{ label: 0, existing: 6, select: 6, new: { size: 12, offset: 0 } }}
                                            onSelectFile={this.handleFileChange}
                                            onRemoveFile={this.handleFileRemove}
                                            versionedFiles={this.state.data.patientSurvivalVersionedFiles}
                                            newFile={this.state.data.patientSurvivalNewFile}
                                            marginBottom={0}
                                            newFileDisplayType={NewFileDisplayType.SrtrData}
                                            onDownloadFile={this.handleFileDownload}
                                        />
                                    </Panel.Body>
                                </Panel>
                            </div>
                        </div>

                        <div className='row'>
                            <div className='col-xs-12'>
                                <div className='row'>
                                    <div className='col-xs-12 col-sm-8 col-sm-offset-2'>
                                        <h4>Graft Survival Data</h4>
                                    </div>
                                </div>

                                <div className='row center-srtr'>
                                    <div className='col-xs-12 col-sm-4 col-sm-offset-2'>
                                        <Panel>
                                            <Panel.Heading>Deceased Donor</Panel.Heading>
                                            <Panel.Body>
                                                <Form horizontal>
                                                    <FormGroup validationState={!this.state.validation.dcAdultGraftSurvival1 ? 'error' : null}>
                                                        <CenterTextInput
                                                            name='dcAdultGraftSurvival1'
                                                            type={DataType.Float}
                                                            labelText='1 Year Adult'
                                                            value={this.state.data.dcAdultGraftSurvival1}
                                                            onChange={this.handleCenterTextInputDataChange} />
                                                    </FormGroup>

                                                    <FormGroup validationState={!this.state.validation.dcAdultGraftSurvival3 ? 'error' : null}>
                                                        <CenterTextInput
                                                            name='dcAdultGraftSurvival3'
                                                            type={DataType.Float}
                                                            labelText='3 Year Adult'
                                                            value={this.state.data.dcAdultGraftSurvival3}
                                                            onChange={this.handleCenterTextInputDataChange} />
                                                    </FormGroup>

                                                    <FormGroup validationState={!this.state.validation.dcPediatricGraftSurvival1 ? 'error' : null}>
                                                        <CenterTextInput
                                                            name='dcPediatricGraftSurvival1'
                                                            type={DataType.Float}
                                                            labelText='1 Year Pediatric'
                                                            value={this.state.data.dcPediatricGraftSurvival1}
                                                            onChange={this.handleCenterTextInputDataChange} />
                                                    </FormGroup>

                                                    <FormGroup validationState={!this.state.validation.dcPediatricGraftSurvival3 ? 'error' : null}>
                                                        <CenterTextInput
                                                            name='dcPediatricGraftSurvival3'
                                                            type={DataType.Float}
                                                            labelText='3 Year Pediatric'
                                                            value={this.state.data.dcPediatricGraftSurvival3}
                                                            onChange={this.handleCenterTextInputDataChange} />
                                                    </FormGroup>
                                                </Form>
                                            </Panel.Body>
                                        </Panel>
                                    </div>

                                    <div className='col-xs-12 col-sm-4'>
                                        <Panel>
                                            <Panel.Heading>Living Donor</Panel.Heading>
                                            <Panel.Body>
                                                <Form horizontal>
                                                    <FormGroup validationState={!this.state.validation.adultGraftSurvival1 ? 'error' : null}>
                                                        <CenterTextInput
                                                            name='adultGraftSurvival1'
                                                            type={DataType.Float}
                                                            labelText='1 Year Adult'
                                                            value={this.state.data.adultGraftSurvival1}
                                                            onChange={this.handleCenterTextInputDataChange} />
                                                    </FormGroup>

                                                    <FormGroup validationState={!this.state.validation.adultGraftSurvival3 ? 'error' : null}>
                                                        <CenterTextInput
                                                            name='adultGraftSurvival3'
                                                            type={DataType.Float}
                                                            labelText='3 Year Adult'
                                                            value={this.state.data.adultGraftSurvival3}
                                                            onChange={this.handleCenterTextInputDataChange} />
                                                    </FormGroup>

                                                    <FormGroup validationState={!this.state.validation.pediatricGraftSurvival1 ? 'error' : null}>
                                                        <CenterTextInput
                                                            name='pediatricGraftSurvival1'
                                                            type={DataType.Float}
                                                            labelText='1 Year Pediatric'
                                                            value={this.state.data.pediatricGraftSurvival1}
                                                            onChange={this.handleCenterTextInputDataChange} />
                                                    </FormGroup>

                                                    <FormGroup validationState={!this.state.validation.pediatricGraftSurvival3 ? 'error' : null}>
                                                        <CenterTextInput
                                                            name='pediatricGraftSurvival3'
                                                            type={DataType.Float}
                                                            labelText='3 Year Pediatric'
                                                            value={this.state.data.pediatricGraftSurvival3}
                                                            onChange={this.handleCenterTextInputDataChange} />
                                                    </FormGroup>
                                                </Form>
                                            </Panel.Body>
                                        </Panel>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className='row'>
                            <div className='col-xs-12'>
                                <div className='row'>
                                    <div className='col-xs-12 col-sm-8 col-sm-offset-2'>
                                        <h4>Patient Survival Data</h4>
                                    </div>
                                </div>

                                <div className='row'>
                                    <div className='col-xs-12 col-sm-4 col-sm-offset-2'>
                                        <Panel>
                                            <Panel.Heading>Deceased Donor</Panel.Heading>
                                            <Panel.Body>
                                                <Form horizontal>
                                                    <FormGroup validationState={!this.state.validation.dcAdultPatientSurvival1 ? 'error' : null}>
                                                        <CenterTextInput
                                                            name='dcAdultPatientSurvival1'
                                                            type={DataType.Float}
                                                            labelText='1 Year Adult'
                                                            value={this.state.data.dcAdultPatientSurvival1}
                                                            onChange={this.handleCenterTextInputDataChange} />
                                                    </FormGroup>
                                                    <FormGroup validationState={!this.state.validation.dcAdultPatientSurvival3 ? 'error' : null}>
                                                        <CenterTextInput
                                                            name='dcAdultPatientSurvival3'
                                                            type={DataType.Float}
                                                            labelText='3 Year Adult'
                                                            value={this.state.data.dcAdultPatientSurvival3}
                                                            onChange={this.handleCenterTextInputDataChange} />
                                                    </FormGroup>

                                                    <FormGroup validationState={!this.state.validation.dcPediatricPatientSurvival1 ? 'error' : null}>
                                                        <CenterTextInput
                                                            name='dcPediatricPatientSurvival1'
                                                            type={DataType.Float}
                                                            labelText='1 Year Pediatric'
                                                            value={this.state.data.dcPediatricPatientSurvival1}
                                                            onChange={this.handleCenterTextInputDataChange} />
                                                    </FormGroup>

                                                    <FormGroup validationState={!this.state.validation.dcPediatricPatientSurvival3 ? 'error' : null}>
                                                        <CenterTextInput
                                                            name='dcPediatricPatientSurvival3'
                                                            type={DataType.Float}
                                                            labelText='3 Year Pediatric'
                                                            value={this.state.data.dcPediatricPatientSurvival3}
                                                            onChange={this.handleCenterTextInputDataChange} />
                                                    </FormGroup>
                                                </Form>
                                            </Panel.Body>
                                        </Panel>
                                    </div>
                                    <div className='col-xs-12 col-sm-4'>
                                        <Panel>
                                            <Panel.Heading>Living Donor</Panel.Heading>
                                            <Panel.Body>
                                                <Form horizontal>
                                                    <FormGroup validationState={!this.state.validation.adultPatientSurvival1 ? 'error' : null}>
                                                        <CenterTextInput
                                                            name='adultPatientSurvival1'
                                                            type={DataType.Float}
                                                            labelText='1 Year Adult'
                                                            value={this.state.data.adultPatientSurvival1}
                                                            onChange={this.handleCenterTextInputDataChange} />
                                                    </FormGroup>

                                                    <FormGroup validationState={!this.state.validation.adultPatientSurvival3 ? 'error' : null}>
                                                        <CenterTextInput
                                                            name='adultPatientSurvival3'
                                                            type={DataType.Float}
                                                            labelText='3 Year Adult'
                                                            value={this.state.data.adultPatientSurvival3}
                                                            onChange={this.handleCenterTextInputDataChange} />
                                                    </FormGroup>

                                                    <FormGroup validationState={!this.state.validation.pediatricPatientSurvival1 ? 'error' : null}>
                                                        <CenterTextInput
                                                            name='pediatricPatientSurvival1'
                                                            type={DataType.Float}
                                                            labelText='1 Year Pediatric'
                                                            value={this.state.data.pediatricPatientSurvival1}
                                                            onChange={this.handleCenterTextInputDataChange} />
                                                    </FormGroup>

                                                    <FormGroup validationState={!this.state.validation.pediatricPatientSurvival3 ? 'error' : null}>
                                                        <CenterTextInput
                                                            name='pediatricPatientSurvival3'
                                                            type={DataType.Float}
                                                            labelText='3 Year Pediatric'
                                                            value={this.state.data.pediatricPatientSurvival3}
                                                            onChange={this.handleCenterTextInputDataChange} />
                                                    </FormGroup>
                                                </Form>
                                            </Panel.Body>
                                        </Panel>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className='row'>
                            <div className='col-xs-12 col-sm-8 col-sm-offset-2'>
                                <Form horizontal>
                                    <FormGroup validationState={!this.state.validation.srtrSourceDate ? 'error' : null}>
                                        <CenterTextInput
                                            name='srtrSourceDate'
                                            type={DataType.String}
                                            labelText='SRTR Source Date:'
                                            dimensions={{ label: 2, field: 6 }}
                                            value={this.state.data.srtrSourceDate}
                                            controlType='date'
                                            onChange={this.handleCenterTextInputDataChange} />
                                    </FormGroup>

                                    <FormGroup validationState={!this.state.validation.centerUnosId ? 'error' : null}>
                                        <CenterTextInput
                                            name='unosId'
                                            type={DataType.String}
                                            labelText='UNOS Center ID:'
                                            dimensions={{ label: 2, field: 6 }}
                                            value={this.state.data.unosId}
                                            onChange={this.handleCenterTextInputDataChange} />
                                    </FormGroup>
                                </Form>
                            </div>
                        </div>
                    </Form>
                </div>
            </>
        );
    }
}

const mapStateToProps = (appState: IApplicationState): IStateProps => {
    return {}
}

const mapDispatchToProps = (dispatch: Dispatch<IApplicationState>): IDispatchProps => {
    return {
        showLoading: (message) => {
            return dispatch(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            dispatch(AppActionCreators.hideLoading());
        },
        getSrtrData: (centerId) => {
            return dispatch(CenterActionCreators.getSrtrData(centerId));
        },
        updateSrtrData: (data) => {
            return dispatch(CenterActionCreators.updateSrtrData(data));
        },
        getVersionedFile: (centerId, id) => {
            return dispatch(CenterActionCreators.getVersionedFile(centerId, id));
        }
    }
}

export default connect<IStateProps, {}, IProps>(
    mapStateToProps,
    mapDispatchToProps)(CenterSrtrDataTab);